/**
 * Vuex 'context' for actions are found here: https://vuex.vuejs.org/api/#actions
 */

import amplitude from '@/analytics/amplitude';
import { AMPLITUDE_EVENTS } from '@/analytics/constants/amplitude-events.constants';
import axios from 'axios';
import humps from 'lodash-humps';
import {
    LOADING,
    SUCCESS,
    ERROR,
} from '@/shared/constants/loadingStatuses.constants';
import { getSentry } from '@/analytics/sentry';
import defaultState from './defaultState';

const AC_BFF_URL = import.meta.env.VITE_ACCOUNT_CENTRAL_BFF_URL;

export default {
    CHARGEBEE_INITIALIZED({ commit }) {
        commit('CHARGEBEE_INITIALIZED');
    },

    LOAD_SUBSCRIPTION_INFO(context, payload) {
        return loadSubscriptionInfo(context, payload);
    },

    LOAD_RENEWAL_ESTIMATE(context, payload) {
        return loadRenewalEstimate(context, payload);
    },

    REMOVE_SCHEDULED_CHANGES(context, payload) {
        removeScheduledChanges(context, payload);
    },

    REMOVE_SCHEDULED_CANCELLATION(context, payload) {
        removeScheduledCancellation(context, payload);
    },

    LOAD_USAGES(context, payload) {
        return loadUsages(context, payload);
    },

    LOAD_HAS_MANAGE_BILLING_PERMISSION(context, payload) {
        return loadManageBillingPermission(context, payload);
    },

    LOAD_ADDONS_PRICING(context, payload) {
        return loadAddonsPricing(context, payload);
    },

    LOAD_CHARGEBEE_PORTAL_SESSION(context, payload) {
        return loadChargebeePortalSession(context, payload);
    },

    SUBMIT_CANCELLATION_REQUEST(context, payload) {
        return submitCancellationRequest(context, payload);
    },

    FETCH_HOSTED_PAGES_SUCCESS_SERVICES(context, payload) {
        return fetchHostedPagesSuccessServices(context, payload);
    },

    LOAD_BILLING_HISTORY(context, payload) {
        return loadBillingHistory(context, payload);
    },

    LOAD_BILLING_INFORMATION(context, payload) {
        return loadBillingInformation(context, payload);
    },
};

const loadBillingHistory = async ({ commit }, { tenantId }) => {
    const sentry = getSentry();

    try {
        commit('SET_BILLING_HISTORY_LOADING', LOADING);
        const loadBillingHistoryPath = `${AC_BFF_URL}/v1/tenants/${tenantId}/billingHistory`;

        const { data } = await axios.get(loadBillingHistoryPath);

        if (data) {
            commit('SET_BILLING_HISTORY', humps(data));
            commit('SET_BILLING_HISTORY_LOADING', SUCCESS);
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error getting billing history for: ${tenantId}` });
        commit('SET_BILLING_HISTORY_LOADING', ERROR);
    }
};

const loadBillingInformation = async ({ commit }, { tenantId }) => {
    const sentry = getSentry();

    try {
        commit('SET_BILLING_INFORMATION_LOADING', LOADING);
        const loadBillingInformationPath = `${AC_BFF_URL}/v1/tenants/${tenantId}/billing-info`;

        const { data } = await axios.get(loadBillingInformationPath);

        if (data) {
            commit('SET_BILLING_INFORMATION', humps(data));
            commit('SET_BILLING_INFORMATION_LOADING', SUCCESS);
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error getting billing information for: ${tenantId}` });
        commit('SET_BILLING_INFORMATION_LOADING', ERROR);
    }
};

const loadSubscriptionInfo = async (context, { tenantId }) => {
    const { commit } = context;
    const sentry = getSentry();

    try {
        commit('SET_SUBSCRIPTION_INFO_LOADING', LOADING);
        const loadSubscriptionInfoPath = `${AC_BFF_URL}/v1/tenants/${tenantId}/subscription-info`;

        const { data } = await axios.get(loadSubscriptionInfoPath);

        if (data) {
            commit('SET_SUBSCRIPTION_INFO', humps(data));
            commit('SET_SUBSCRIPTION_INFO_LOADING', SUCCESS);
        } else {
            commit('SET_SUBSCRIPTION_INFO_LOADING', ERROR);
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error fetching subscription info for: ${tenantId}` });
        commit('SET_SUBSCRIPTION_INFO_LOADING', ERROR);
    }
};

const loadRenewalEstimate = async (context, { tenantId }) => {
    const { commit } = context;
    const sentry = getSentry();

    try {
        commit('SET_RENEWAL_ESTIMATE_LOADING', LOADING);
        const loadRenewalEstimatePath = `${AC_BFF_URL}/v1/tenants/${tenantId}/renewal-estimate`;

        const { data } = await axios.get(loadRenewalEstimatePath);

        if (data) {
            commit('SET_RENEWAL_ESTIMATE', humps(data));
            commit('SET_RENEWAL_ESTIMATE_LOADING', SUCCESS);
        } else {
            sentry.captureException(new Error(`Error fetching renewal estimate, empty body returned for ${tenantId}`));
            commit('SET_RENEWAL_ESTIMATE_LOADING', ERROR);
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error fetching renewal estimate, http exception for ${tenantId}` });
        commit('SET_RENEWAL_ESTIMATE_LOADING', ERROR);
    }
};

const removeScheduledChanges = async (context, { tenantId }) => {
    const { commit } = context;
    const sentry = getSentry();
    commit('SET_CANCEL_SCHEDULED_CHANGES_LOADING', LOADING);

    try {
        const removeScheduledChangesPath = `${AC_BFF_URL}/v1/tenants/${tenantId}/subscription-scheduled-changes`;

        const { status } = await axios.delete(removeScheduledChangesPath);

        if (status === 204) {
            commit('SET_CANCEL_SCHEDULED_CHANGES_LOADING', SUCCESS);
            amplitude.logEvent(AMPLITUDE_EVENTS.SUBSCRIPTION_DETAILS_CANCEL_SCHEDULED_CHANGES_SUCCESS, {
                'Event Source': 'Subscription Details Modal',
            });
        } else {
            sentry.captureException(new Error(`Error removing scheduled changes, non 204 status returned for ${tenantId}`));
            commit('SET_CANCEL_SCHEDULED_CHANGES_LOADING', ERROR);
            amplitude.logEvent(AMPLITUDE_EVENTS.SUBSCRIPTION_DETAILS_CANCEL_SCHEDULED_CHANGES_FAILURE, {
                'Event Source': 'Subscription Details Modal',
            });
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error removing scheduled changes, http exception for ${tenantId}` });
        commit('SET_CANCEL_SCHEDULED_CHANGES_LOADING', ERROR);
        amplitude.logEvent(AMPLITUDE_EVENTS.SUBSCRIPTION_DETAILS_CANCEL_SCHEDULED_CHANGES_FAILURE, {
            'Event Source': 'Subscription Details Modal',
        });
    }
};

const removeScheduledCancellation = async (context, { tenantId }) => {
    const { commit } = context;
    const sentry = getSentry();
    commit('SET_REMOVE_SCHEDULED_CANCELLATION_LOADING', LOADING);

    try {
        const removeScheduledCancellationPath = `${AC_BFF_URL}/v1/tenants/${tenantId}/scheduled-cancellation`;

        const { status } = await axios.delete(removeScheduledCancellationPath);

        if (status === 204) {
            commit('SET_REMOVE_SCHEDULED_CANCELLATION_LOADING', SUCCESS);
        } else {
            sentry.captureException(new Error(`Error removing scheduled cancellation, non 204 status returned for ${tenantId}. Status: ${status}`));
            commit('SET_REMOVE_SCHEDULED_CANCELLATION_LOADING', ERROR);
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error removing scheduled cancellation, http exception for ${tenantId}` });
        commit('SET_REMOVE_SCHEDULED_CANCELLATION_LOADING', ERROR);
    }
};

const loadUsages = async (context, { tenantId }) => {
    const { commit } = context;
    const sentry = getSentry();
    commit('SET_USAGES_LOADING', LOADING);

    try {
        const loadUsagesPath = `${AC_BFF_URL}/v1/usage/${tenantId}/usages`;

        const { data } = await axios.get(loadUsagesPath);

        if (data) {
            commit('SET_USAGES', humps(data));
            commit('SET_USAGES_LOADING', SUCCESS);
        } else {
            sentry.captureException(new Error(`Error loading usages, empty body returned for ${tenantId}`));
            commit('SET_USAGES_LOADING', ERROR);
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error loading usages, http exception for ${tenantId}` });
        commit('SET_USAGES_LOADING', ERROR);
    }
};

const loadManageBillingPermission = async (context, { tenantId }) => {
    const { commit } = context;
    commit('SET_MANAGE_BILLING_PERMISSION_LOADING', LOADING);

    try {
        const loadUsagesPath = `${AC_BFF_URL}/v1/tenants/${tenantId}/hasManageBillingPermission`;

        await axios.get(loadUsagesPath);

        commit('SET_HAS_MANAGE_BILLING', true);
        commit('SET_MANAGE_BILLING_PERMISSION_LOADING', SUCCESS);
    } catch (e) {
        if (e.response && e.response.status === 403) {
            // 403 Forbidden means the user does not have permission to manage billing
            commit('SET_HAS_MANAGE_BILLING', false);
            commit('SET_MANAGE_BILLING_PERMISSION_LOADING', SUCCESS);
            return;
        }

        const sentry = getSentry();
        sentry.captureException(e, { message: `Error loading usages, http exception for ${tenantId}` });
        commit('SET_HAS_MANAGE_BILLING', false);
        commit('SET_MANAGE_BILLING_PERMISSION_LOADING', ERROR);
    }
};

const loadAddonsPricing = async (context, { tenantId }) => {
    const validateData = (data) => {
        const addonsPricing = humps(data);
        if (!addonsPricing) {
            return false;
        }
        const keys = Object.keys(addonsPricing);
        const expectedKeys = Object.keys(defaultState.addonsPricing);
        return expectedKeys.every((key) => keys.includes(key) && !!addonsPricing[key]);
    };

    const { commit } = context;
    try {
        commit('SET_ADDONS_PRICING_LOADING', LOADING);

        const loadSubscriptionInfoPath = `${AC_BFF_URL}/v1/prices/${tenantId}/editable-addons`;

        const { data } = await axios.get(loadSubscriptionInfoPath);

        if (validateData(data)) {
            commit('SET_ADDONS_PRICING', humps(data));
            commit('SET_ADDONS_PRICING_LOADING', SUCCESS);
        } else {
            throw new Error('Invalid pricing data');
        }
    } catch (e) {
        const sentry = getSentry();
        sentry.captureException(e, { message: `Error fetching addon pricing for: ${tenantId}` });
        commit('SET_ADDONS_PRICING_LOADING', ERROR);
    }
};

const loadChargebeePortalSession = async (context, { tenantId, portalType }) => {
    try {
        const loadChargebeePortalSessionPath = `${AC_BFF_URL}/v1/subscriptionManagement/portalSession/${portalType}`;

        const { data } = await axios.get(loadChargebeePortalSessionPath, {
            headers: {
                'x-is-tenant-id': tenantId,
            },
        });

        return humps(data);
    } catch (e) {
        const sentry = getSentry();
        sentry.captureException(e);

        throw e;
    }
};

const submitCancellationRequest = async (context, { tenantId, cancellationReason, cancellationComment }) => {
    const submitCancellationRequestPath = `${AC_BFF_URL}/v1/tenants/${tenantId}/cancelForm`;
    const { commit } = context;
    const sentry = getSentry();

    commit('SET_CANCEL_REQUEST_LOADING', LOADING);
    try {
        const { status } = await axios.post(
            submitCancellationRequestPath,
            {
                cancellation_reason: cancellationReason,
                cancellation_comment: cancellationComment,
            },
        );
        if (status === 204) {
            commit('SET_CANCEL_REQUEST_LOADING', SUCCESS);
            amplitude.logEvent(AMPLITUDE_EVENTS.REQUEST_TO_CANCEL_SUCCESS, {
                'Event Source': 'Request to Cancel Modal',
            });
        } else {
            sentry.captureException(new Error(`Error requesting cancellation, non 204 status returned for ${tenantId}`));
            commit('SET_CANCEL_REQUEST_LOADING', ERROR);
            amplitude.logEvent(AMPLITUDE_EVENTS.REQUEST_TO_CANCEL_FAILURE, {
                'Event Source': 'Request to Cancel Modal',
            });
        }
    } catch (e) {
        sentry.captureException(e, { message: `Error requesting cancellation, http exception for ${tenantId}` });
        commit('SET_CANCEL_REQUEST_LOADING', ERROR);
        amplitude.logEvent(AMPLITUDE_EVENTS.REQUEST_TO_CANCEL_FAILURE, {
            'Event Source': 'Request to Cancel Modal',
        });
    }
};

const fetchHostedPagesSuccessServices = async (context, { tenantId, level }) => {
    const { commit } = context;

    try {
        commit('SET_CHARGEBEE_HOSTED_PORTAL_LOADING', LOADING);
        const loadChargebeeHostedPortalSessionPath = `${AC_BFF_URL}/v1/subscriptionManagement/hosted-pages/success-services-addon`;

        const { data } = await axios.get(loadChargebeeHostedPortalSessionPath,
            {
                headers: {
                    'x-is-tenant-id': tenantId,
                },
                params: {
                    tenantId: tenantId,
                    level: level,
                },
            });

        const massagedData = humps(data);

        if (data) {
            commit('SET_CHARGEBEE_HOSTED_PORTAL_LOADING', SUCCESS);
        } else {
            commit('SET_CHARGEBEE_HOSTED_PORTAL_LOADING', ERROR);
        }

        return massagedData;
    } catch (e) {
        commit('SET_CHARGEBEE_HOSTED_PORTAL_LOADING', ERROR);
        const sentry = getSentry();
        sentry.captureException(e);

        throw e;
    }
};
